"use client";

import type { GenerationHistoryItem } from "@/hooks/use-generation-history";

// ── Helpers ────────────────────────────────────────────────────

function timeAgo(timestamp: number): string {
  const seconds = Math.floor((Date.now() - timestamp) / 1000);
  if (seconds < 60) return "a l'instant";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `il y a ${minutes}min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `il y a ${hours}h`;
  const days = Math.floor(hours / 24);
  return `il y a ${days}j`;
}

function truncatePrompt(prompt: string, maxLen: number): string {
  if (prompt.length <= maxLen) return prompt;
  return prompt.slice(0, maxLen).trimEnd() + "...";
}

// ── Types ──────────────────────────────────────────────────────

interface GenerationHistoryProps {
  items: GenerationHistoryItem[];
  onSelect: (item: GenerationHistoryItem) => void;
  onRemove: (id: string) => void;
}

// ── Component ──────────────────────────────────────────────────

export function GenerationHistory({
  items,
  onSelect,
  onRemove,
}: GenerationHistoryProps) {
  if (items.length === 0) return null;

  return (
    <div className="flex flex-col gap-2">
      <span className="text-[10px] font-semibold uppercase tracking-[0.15em] text-text-muted">
        Recents
      </span>
      <div className="flex flex-col gap-1 max-h-[180px] overflow-y-auto"
        style={{
          scrollbarWidth: "thin",
          scrollbarColor: "rgba(139,92,246,0.2) transparent",
        }}
      >
        {items.map((item) => (
          <div key={item.id} className="group relative">
            <button
              onClick={() => onSelect(item)}
              className="w-full flex items-start gap-2.5 px-2.5 py-2 rounded-lg text-left transition-all duration-150 cursor-pointer hover:bg-white/[0.04]"
              style={{
                border: "1px solid rgba(255,255,255,0.04)",
              }}
            >
              {/* Icon */}
              <div
                className="w-5 h-5 rounded-md flex items-center justify-center shrink-0 mt-0.5"
                style={{ backgroundColor: "rgba(139,92,246,0.1)" }}
              >
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#a78bfa" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 18V5l12-2v13" />
                  <circle cx="6" cy="18" r="3" />
                  <circle cx="18" cy="16" r="3" />
                </svg>
              </div>

              {/* Text */}
              <div className="flex-1 min-w-0">
                <p className="text-[12px] font-medium text-text-primary truncate">
                  {truncatePrompt(item.prompt, 40)}
                </p>
                <p className="text-[10px] text-text-muted mt-0.5">
                  {item.bpm} BPM &middot; {item.measures} mesures &middot; {timeAgo(item.timestamp)}
                </p>
              </div>
            </button>

            {/* Remove button, visible on hover */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                onRemove(item.id);
              }}
              className="absolute top-1.5 right-1.5 w-5 h-5 rounded flex items-center justify-center text-text-muted hover:text-text-primary hover:bg-white/[0.06] transition-all duration-100 cursor-pointer opacity-0 group-hover:opacity-100"
              title="Supprimer"
            >
              <svg width="10" height="10" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                <path d="M4 4l6 6M10 4l-6 6" />
              </svg>
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
